import { useCallback, useEffect, useState } from 'react';
import { DeepReadonlyObject, RxCollection, RxDocument } from 'rxdb';
import { BehaviorSubject, combineLatest } from 'rxjs';

import {
	Booking,
	BookingStateEnum,
	DataChange,
	ICheckInPassengerOptions,
	NormalizedScheduledLeg,
} from '@mopla/data-models';

import {
	editBooking as editBookingAction,
	editLeg as editLegAction,
	initEventStream as initEventStreamAction,
	initPushProcessor as initPushProcessorAction,
	resetScheduleDiff,
} from '../actions/scheduleActions';
import { useBusinessLayer } from '../business-logic';
import { ScheduleDiffEl } from '../entities/scheduleDiff';
import { mergeBookingsWithChanges } from '../utils/mergeBookingsWithChanges';
import { mergeLegsWithChanges } from '../utils/mergeLegsWithChanges';

import { useCheckInFlowStore } from './useCheckInFlowStore';
import { useScheduleChangesStore } from './useScheduleChangesStore';

export type ScheduleValue = {
	legs: NormalizedScheduledLeg[] | null;
	bookings: Booking[] | null;
	editLeg: (leg: NormalizedScheduledLeg) => void;
	initEventStream: () => void;
	initPushProcessor: () => void;
	markChangesAsSeen: () => void;
	unseenChanges: ScheduleDiffEl[] | null;
	/** A push asked to show the changes and there are some. */
	showScheduleChanges: boolean;
	initialized: boolean;
	currentCheckInBookings: Booking[];
	/** The fn updates a booking in the react storage and creates a DataChange object which will be upserted into the rxdb when `stopCheckIn` is called*/
	onCheckInBookingChanged: (
		booking: Booking,
		newState: BookingStateEnum,
		options?: ICheckInPassengerOptions
	) => void;
	checkInLegId: string | null;
	setCheckInLegId(checkInLegId: string | null): void;
	isCheckInActive: boolean;
	startCheckIn: (scheduledLegId: string) => void;
	submitCheckIn: () => void;
	closeCheckIn: () => void;
	currentCheckInBookingsGrouped: {
		cancelledBookings: Booking[];
		checkedBookings: Booking[];
		notCheckedBookings: Booking[];
		allBookingsCheckedIn: boolean;
		bookingsCount: number;
	};
};

export const useSchedule = (): ScheduleValue => {
	const businessLayer = useBusinessLayer();

	const [legs, setLegs] = useState<NormalizedScheduledLeg[] | null>(null);
	const [initialized, setInitialized] = useState(false);
	const [unseenChanges, setUnseenChanges] = useState<ScheduleDiffEl[] | null>(
		null
	);
	const [bookings, setBookings] = useState<Booking[] | null>(null);

	/** This store must not be updated by the incoming rxdb bookings or schedule updates */
	const store = useCheckInFlowStore();
	const isShowRequested = useScheduleChangesStore((s) => s.isShowRequested);

	const initEventStream = () => {
		if (businessLayer.dispatch) {
			businessLayer.dispatch(initEventStreamAction());
		}
	};

	const initPushProcessor = () => {
		if (businessLayer.dispatch) {
			businessLayer.dispatch(initPushProcessorAction());
		}
	};

	const editLeg = useCallback(
		(leg: NormalizedScheduledLeg) => {
			businessLayer.dispatch(editLegAction(leg));
		},
		[businessLayer]
	);

	const markChangesAsSeen = () => {
		if (businessLayer.dispatch) {
			businessLayer.dispatch(resetScheduleDiff());
		}
	};

	useEffect(() => {
		/** Early return. We do not allow updating the locally stored bookings during the CheckIn flow by the data from API: /schedule, /bookingLegs */
		if (store.isCheckInActive) {
			return;
		}

		if (store.checkInLegId) {
			const legToCheckin = legs?.find(
				(l) => l.scheduledLegId === store.checkInLegId
			);

			store.setCurrentCheckInBookings(
				bookings?.filter((b) =>
					legToCheckin?.enteringBookings?.includes(b.bookingId)
				) || []
			);
		} else {
			store.setCurrentCheckInBookings([]);
		}
	}, [store.checkInLegId, bookings, legs, store.isCheckInActive]);

	useEffect(() => {
		const scheduledLegCollection = businessLayer.db[
			'scheduledLeg'
		] as RxCollection<NormalizedScheduledLeg>;

		const bookingCollection = businessLayer.db[
			'booking'
		] as RxCollection<Booking>;

		const legsLiveQuery = scheduledLegCollection.find().$;
		const bookingsLiveQuery = bookingCollection.find().$;
		const dataChangesQuery = businessLayer.db['dataChanges'].find().$;

		const changesMergeSource = combineLatest([
			legsLiveQuery as BehaviorSubject<RxDocument<NormalizedScheduledLeg>[]>,
			bookingsLiveQuery as BehaviorSubject<RxDocument<Booking>[]>,
			dataChangesQuery as BehaviorSubject<RxDocument<DataChange>[]>,
		]);

		const rxdbSubscr2 = changesMergeSource.subscribe((val) => {
			const [legsDocs, bookingsDocs, dataChangesDocs] = val;
			setInitialized(true);
			const originalLegs = legsDocs.map((l) => l.toJSON());
			const originalBookings = bookingsDocs.map((l) => l.toJSON());
			const dataChanges = dataChangesDocs.map((l) => l.toJSON());

			const legsChanges = filterLegsDataChanges(dataChanges);
			const bookingsChanges = filterBookingsDataChanges(dataChanges);

			const updatedLegs = mergeLegsWithChanges(originalLegs, legsChanges);

			const updatedBookings = mergeBookingsWithChanges(
				originalBookings,
				bookingsChanges
			);

			setBookings(updatedBookings);
			setLegs(updatedLegs);
		});

		/** TODO can be moved outside of this hook */
		const unseenChanges = businessLayer.db['scheduleDiff'].find().$;

		const rxdbSubscr1 = unseenChanges.subscribe(
			(val: RxDocument<ScheduleDiffEl>[]) => {
				setUnseenChanges(val.map((l) => l.toMutableJSON()));
			}
		);

		return () => {
			rxdbSubscr1.unsubscribe();
			rxdbSubscr2.unsubscribe();
		};
	}, [businessLayer.db]);

	const handleSubmitCheckIn = useCallback(() => {
		store.stopCheckIn();

		/** Adding the DataChange documents will initiate the API interactions (calls to the /checkInPassenger) */
		store.dataChanges.forEach((dataChange) => {
			businessLayer.dispatch(editBookingAction(dataChange));
		});

		store.cleanUpDataChanges();
	}, [businessLayer, store]);

	const handleCloseCheckIn = useCallback(() => {
		store.stopCheckIn();

		store.cleanUpDataChanges();
	}, [store]);

	return {
		legs,
		bookings,
		editLeg,
		initEventStream: initEventStream,
		initPushProcessor,
		unseenChanges,
		showScheduleChanges: isShowRequested && !!unseenChanges?.length,
		markChangesAsSeen,
		initialized,
		currentCheckInBookings: store.currentCheckInBookings,
		checkInLegId: store.checkInLegId,
		setCheckInLegId: store.setCheckInLegId,
		onCheckInBookingChanged: store.updateBooking,
		isCheckInActive: store.isCheckInActive,
		startCheckIn: store.startCheckIn,
		submitCheckIn: handleSubmitCheckIn,
		closeCheckIn: handleCloseCheckIn,
		currentCheckInBookingsGrouped: store.currentCheckInBookingsGrouped,
	};
};

export function filterLegsDataChanges(
	dataChanges: DeepReadonlyObject<DataChange>[]
) {
	return dataChanges.filter((ch) => ch.scheduledLegId && !ch.bookingId);
}

export function filterBookingsDataChanges(
	dataChanges: DeepReadonlyObject<DataChange>[]
) {
	return dataChanges.filter((ch) => ch.bookingId);
}
